// The {{Column:aggregation}} grammar text charts embed, e.g. {{InvoiceAmount:min}},
// plus the bare {{count}} of rows, which belongs to no column.
//
// The name is the column's accessor_key with its section abbreviation stripped
// (apInvoiceAmount -> InvoiceAmount): accessor keys are stable, while display
// names are municipality-configurable and would break every saved report on a
// rename.
//
// Mirrors api/app/services/charts/data_field_tokens.rb — the two have to agree
// or a report reads one way on screen and another in its PDF.

import moment from 'moment'

// `count` stays valid on a column so saved {{X:count}} text keeps resolving; the
// editor offers the bare {{count}} instead, since it counts the same rows
// whatever the column.
export const AGGREGATIONS = ['min', 'max', 'sum', 'avg', 'count', 'distinct']

export const COUNT = 'count'

const DATE_LIKE = /^\d{4}-\d{2}-\d{2}(?:T.*)?$/

export const AGGREGATION_LABELS = {
  min: 'Minimum',
  max: 'Maximum',
  sum: 'Sum',
  avg: 'Average',
  count: 'Count',
  distinct: 'Distinct count'
}

// A factory: the regex is global, so a shared instance would carry lastIndex
// between callers and make matches skip.
export const dataFieldTokenPattern = () =>
  new RegExp(
    `\\{\\{\\s*(?:([A-Za-z0-9_]+):(${AGGREGATIONS.join('|')})|(${COUNT}))\\s*\\}\\}`,
    'g'
  )

// [name, aggregation] from a pattern match (or a replace callback's arguments).
// The bare {{count}} has no name.
export const tokenParts = (match) => (match[3] ? [null, COUNT] : [match[1], match[2]])

export const hasDataFieldTokens = (text) =>
  !!text && dataFieldTokenPattern().test(text)

export const dataFieldToken = (name, aggregation) =>
  name ? `{{${name}:${aggregation}}}` : `{{${aggregation}}}`

export const dataFieldKey = (name, aggregation) => (name ? `${name}:${aggregation}` : aggregation)

export const COUNT_TOKEN = dataFieldToken(null, COUNT)

export const shortColumnName = (column) => {
  const prefix = column?.section_abbreviation?.toLowerCase() || ''
  const accessorKey = column?.accessor_key || ''
  if (!prefix || !accessorKey.toLowerCase().startsWith(prefix)) return accessorKey
  return accessorKey.slice(prefix.length)
}

export const columnForName = (name, columns = []) => {
  const needle = String(name).toLowerCase()
  return (columns || []).find(
    (column) =>
      shortColumnName(column).toLowerCase() === needle ||
      (column?.accessor_key || '').toLowerCase() === needle
  )
}

// Plain delimited numbers, no currency symbol: the author writes the "$" into
// the sentence, and Charts::FilterInterpolator#format_aggregate has to produce
// the same string or the PDF and the screen disagree.
export const formatAggregate = (value) => {
  if (value === undefined || value === null || value === '') return ''

  // A min/max over a date column arrives as the ISO string the API serialized.
  if (value instanceof Date) return moment(value).format('M/D/YYYY')
  if (typeof value === 'string' && DATE_LIKE.test(value)) return moment(value).format('M/D/YYYY')

  const number = typeof value === 'number' ? value : Number(value)
  if (Number.isNaN(number)) return String(value)

  return number.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })
}
